import api, { setTokens, clearTokens, getRefreshToken } from '../api';

/**
 * Auth Service
 * Handles all authentication-related API calls
 */

const authService = {
  /**
   * Sign up with email and password
   * @param {Object} data - { email, password, full_name }
   * @returns {Promise<Object>} User data
   */
  signup: async (data) => {
    try {
      const response = await api.post('/auth/signup', data);
      // Store tokens in localStorage
      if (response.access_token && response.refresh_token) {
        setTokens(response.access_token, response.refresh_token);
      }
      return response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Login with email and password
   * @param {Object} data - { email, password }
   * @returns {Promise<Object>} User data
   */
  login: async (data) => {
    try {
      const response = await api.post('/auth/login', data);
      // Store tokens in localStorage
      if (response.access_token && response.refresh_token) {
        setTokens(response.access_token, response.refresh_token);
      }
      return response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Initiate Google OAuth flow
   * @returns {Promise<Object>} Google OAuth URL
   */
  googleAuth: async () => {
    try {
      const response = await api.post('/auth/google');
      return response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Handle OAuth callback (after Google redirect)
   * @param {Object} data - { access_token, refresh_token }
   * @returns {Promise<Object>} User data
   */
  handleOAuthCallback: async (data) => {
    try {
      const response = await api.post('/auth/callback', data);
      // Store tokens in localStorage
      if (response.access_token && response.refresh_token) {
        setTokens(response.access_token, response.refresh_token);
      }
      return response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Logout current user
   * @returns {Promise<Object>} Success message
   */
  logout: async () => {
    try {
      const response = await api.post('/auth/logout');
      // Clear tokens from localStorage
      clearTokens();
      return response;
    } catch (error) {
      // Clear tokens even if API call fails
      clearTokens();
      throw error;
    }
  },

  /**
   * Get current authenticated user
   * @returns {Promise<Object>} User data
   */
  getCurrentUser: async () => {
    try {
      const response = await api.get('/auth/me');
      return response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Refresh access token
   * @returns {Promise<Object>} Success message with new tokens
   */
  refreshToken: async () => {
    try {
      const refreshToken = getRefreshToken();
      const response = await api.post('/auth/refresh', { refresh_token: refreshToken });
      // Store new tokens
      if (response.access_token && response.refresh_token) {
        setTokens(response.access_token, response.refresh_token);
      }
      return response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Resend email verification
   * @param {string} email - User's email
   * @returns {Promise<Object>} Success message
   */
  resendVerification: async (email) => {
    try {
      const response = await api.post('/auth/resend-verification', { email });
      return response;
    } catch (error) {
      throw error;
    }
  }
};

export default authService;